/**
 * 1:1 한 턴을 SSE 로 받습니다 (히어로챗 lib/chatStream.js · lib/sse.js 와 같은 방식).
 *
 * EventSource 는 POST 와 Authorization 머리글을 못 보내서 fetch 의 본문 스트림을 직접 읽습니다.
 *
 * 이벤트: accepted · thinking · delta · done · stopped · error
 * 스트림이 done · stopped · error 없이 끝나면 onCut 을 부릅니다(연결이 끊김). 화면은 대화를 다시
 * 불러와 서버에 저장된 상태로 맞춥니다.
 */
import { API_BASE, ApiError, authHeaders, notifyAuthError } from './client.js';

const TERMINAL_EVENTS = new Set(['done', 'stopped', 'error']);

/** 쌓인 글에서 완성된 이벤트를 꺼냅니다. 남은 조각은 rest 로 돌려줍니다 */
export function parseSseChunk(buffer) {
    const events = [];
    let rest = buffer.replace(/\r\n/g, '\n');
    let boundary = rest.indexOf('\n\n');
    while (boundary >= 0) {
        const block = rest.slice(0, boundary);
        rest = rest.slice(boundary + 2);
        let name = 'message';
        const dataLines = [];
        for (const line of block.split('\n')) {
            if (line.startsWith(':')) {
                continue;
            }
            if (line.startsWith('event:')) {
                name = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trimStart());
            }
        }
        if (dataLines.length) {
            try {
                events.push({ name, data: JSON.parse(dataLines.join('\n')) });
            } catch (error) {
                console.error('[chat] 이벤트를 읽지 못함', name, error.message);
            }
        }
        boundary = rest.indexOf('\n\n');
    }
    return { events, rest };
}

/**
 * @param {string} memberKey
 * @param {{ body: string, clientKey: string }} payload
 * @param {{ signal: AbortSignal, onEvent: (name: string, data: object) => void, onCut: () => void }} handlers
 */
export async function streamChatTurn(memberKey, payload, { signal, onEvent, onCut }) {
    const response = await fetch(`${API_BASE}/chats/${encodeURIComponent(memberKey)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...authHeaders() },
        body: JSON.stringify(payload),
        signal,
    });

    // 스트림을 열기 전의 오류(입력 검사, 401 등)는 JSON 으로 옵니다.
    if (!response.ok || !response.headers.get('content-type')?.includes('text/event-stream')) {
        let body = null;
        try {
            body = await response.json();
        } catch {
            body = null;
        }
        const error = new ApiError(response.status, body);
        if (response.status === 401) {
            notifyAuthError(error);
        }
        throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finished = false;
    try {
        for (;;) {
            const { value, done } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const { events, rest } = parseSseChunk(buffer);
            buffer = rest;
            for (const event of events) {
                if (TERMINAL_EVENTS.has(event.name)) {
                    finished = true;
                }
                onEvent(event.name, event.data);
            }
        }
    } catch (error) {
        if (signal?.aborted) {
            return;
        }
        throw error;
    }
    if (!finished && !signal?.aborted) {
        onCut();
    }
}
